import { createSelector } from '@reduxjs/toolkit'
import { pipe } from 'ramda'
import { filterIf } from 'utils/fp'
import createSorter, { SortConfig } from 'core/helpers/createSorter'
import DataKeys from 'k8s/DataKeys'
import getDataSelector from 'core/utils/getDataSelector'
import { IRolesSelector } from './model'
import { selectParamsFromProps, createSharedSelector } from 'core/utils/selectorHelpers'

export const managementRolesSelector = createSharedSelector(
  getDataSelector<DataKeys.ManagementRoles>(DataKeys.ManagementRoles),
  (rawRoles) => {
    // associate nodes with the combinedHost entry
    return rawRoles.map((role) => ({
      ...role,
      displayName:
        role.displayName || validOverrides.includes(role.name)
          ? hardcodedKubeRolesNames[role.name]
          : role.name,
      description: validOverrides.includes(role.name)
        ? hardcodedKubeRolesDescriptions[role.name]
        : role.description,
    }))
  },
)

export const makeManagementRolesSelector = (
  defaultParams = {
    orderBy: 'name',
    orderDirection: 'desc',
  } as SortConfig & { allRoles?: boolean },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSelector(managementRolesSelector, selectParams, (roles, params) => {
    const { allRoles, orderBy, orderDirection } = params
    return pipe<IRolesSelector[], IRolesSelector[], IRolesSelector[]>(
      filterIf(!allRoles, (role) => validOverrides.includes(role.name)),
      createSorter({ orderBy, orderDirection }),
    )(roles)
  })
}

// Generally not a fan of this, but we dont have a lot of options.
// Need to update the backend database records to have an actual description / friendly name
const hardcodedKubeRolesDescriptions = {
  admin:
    'An Administrator has global access to all regions, tenants and all resources within your Platform9 environment, and access to perform all operations. As a Platform9 Managed Kubernetes (PMK) administrator you can invite more users to your PMK cloud environment, create or delete tenants, create or delete clusters, deploy workloads on clusters, assign RBAC policies to self-service users so they can access clusters,  etc.',
  _member_:
    'A self-service user has limited access to the Platform9 Managed Kubernetes (PMK) cloud environment. When an Administrator invites a self-service user to the PMK cloud environment, they can start by assigning the user to one or more tenants. The user will only be able to log into those tenants. Within the tenant, a self-service user will by default not have access to any clusters created in that tenant. The administrator will need to create specific RBAC policies to give the user access to one or more clusters within the tenant, before the users can perform any operations on the clusters.',
  reader: 'A read-only user can view Kubernetes objects, but cannot create or update them.',
}

const hardcodedKubeRolesNames = {
  admin: 'Administrator',
  _member_: 'Self-service User',
  reader: 'Read Only User',
}
const validOverrides = Object.keys(hardcodedKubeRolesNames)
